import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

const empty = {
  firm_name: "TAXINPHO",
  address: "",
  state: "",
  contact: "",
  email: "",
  gstin: "",
  pan: "",
  signature_text: "Authorized Signatory",
  signature_image: "",
  invoice_terms: "Please pay the amount within 7 days.",
};

export default function FirmSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/firm-settings").then(r => setForm({ ...empty, ...r.data }));
  }, []);

  const onSigUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Image too large (max 500 KB). Try a smaller PNG.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, signature_image: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/firm-settings", form);
      setForm({ ...empty, ...data });
      toast.success("Firm settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="mb-8">
        <p className="label-caps mb-2">Firm Profile</p>
        <h1 className="font-display font-black text-4xl tracking-tighter">Invoice Branding</h1>
        <p className="text-zinc-600 mt-2">These details appear on every invoice PDF.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <div className="bg-white border border-zinc-200 p-6 space-y-4">
          <h2 className="font-display font-bold text-xl tracking-tight">Firm Identity</h2>
          <div>
            <label className="label-caps block mb-2">Firm / Practice Name *</label>
            <input required value={form.firm_name} onChange={e => setForm({ ...form, firm_name: e.target.value })} disabled={!isAdmin} data-testid="firm-name" className="w-full border border-zinc-300 px-3 py-2 disabled:bg-zinc-50" />
          </div>
          <div>
            <label className="label-caps block mb-2">Address</label>
            <textarea rows="2" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 disabled:bg-zinc-50" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-2">State</label>
              <input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 disabled:bg-zinc-50" />
            </div>
            <div>
              <label className="label-caps block mb-2">Contact No.</label>
              <input value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 font-mono disabled:bg-zinc-50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-2">GSTIN</label>
              <input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 font-mono disabled:bg-zinc-50" />
            </div>
            <div>
              <label className="label-caps block mb-2">PAN</label>
              <input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 font-mono disabled:bg-zinc-50" />
            </div>
          </div>
          <div>
            <label className="label-caps block mb-2">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 disabled:bg-zinc-50" />
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-6 space-y-4">
          <h2 className="font-display font-bold text-xl tracking-tight">Signature</h2>
          <p className="text-sm text-zinc-600">Upload your signature image (PNG with transparent bg recommended, max 500 KB). It will appear above the signature line on every invoice.</p>
          {form.signature_image ? (
            <div className="bg-zinc-50 border border-zinc-200 p-4 flex items-center gap-4">
              <img src={form.signature_image} alt="Signature" className="h-20 bg-white border border-zinc-200 p-2 object-contain" />
              <div className="flex-1">
                <p className="text-sm font-medium">Current signature</p>
                <p className="text-xs text-zinc-500">Will be embedded on every invoice PDF.</p>
              </div>
              {isAdmin && (
                <button type="button" onClick={() => setForm({ ...form, signature_image: "" })} className="p-2 hover:bg-red-50 text-[#D92D20]" data-testid="remove-signature">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ) : (
            <label className={`block border-2 border-dashed border-zinc-300 p-6 text-center ${isAdmin ? "cursor-pointer hover:border-zinc-900" : "opacity-50"}`}>
              <Upload size={20} className="mx-auto mb-2 text-zinc-500" />
              <p className="text-sm font-semibold">Click to upload signature</p>
              <p className="text-xs text-zinc-500 mt-1">PNG / JPG · Max 500 KB</p>
              <input type="file" accept="image/png,image/jpeg" onChange={onSigUpload} disabled={!isAdmin} className="hidden" data-testid="signature-upload" />
            </label>
          )}
          <div>
            <label className="label-caps block mb-2">Signature Text</label>
            <input value={form.signature_text} onChange={e => setForm({ ...form, signature_text: e.target.value })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 disabled:bg-zinc-50" />
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-6 space-y-4">
          <h2 className="font-display font-bold text-xl tracking-tight">Invoice Terms</h2>
          <textarea rows="2" value={form.invoice_terms} onChange={e => setForm({ ...form, invoice_terms: e.target.value })} disabled={!isAdmin} className="w-full border border-zinc-300 px-3 py-2 disabled:bg-zinc-50" />
        </div>

        {isAdmin ? (
          <button type="submit" disabled={busy} data-testid="save-firm" className="bg-zinc-900 text-white px-6 py-3 font-semibold hover:bg-zinc-800 disabled:opacity-50">
            {busy ? "Saving..." : "Save firm settings"}
          </button>
        ) : (
          <p className="text-sm text-zinc-500">Only admins can edit firm settings.</p>
        )}
      </form>
    </div>
  );
}
